import React, { useState } from 'react';
import { ShoppingBag, Upload } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PosFilters } from '../components/pos/PosFilters';
import { OverviewTab } from '../components/pos/OverviewTab';
import { MonthwiseTab } from '../components/pos/MonthwiseTab';
import { RepeatTab } from '../components/pos/RepeatTab';
import { MissingTab } from '../components/pos/MissingTab';
import { CustomersTab } from '../components/pos/CustomersTab';
import { PointsTab } from '../components/pos/PointsTab';
import { HistoryTab } from '../components/pos/HistoryTab';
import { ImportHistoryTab } from '../components/pos/ImportHistoryTab';
import { ImportDialog } from '../components/pos/ImportDialog';
import { CustomerSheet } from '../components/pos/CustomerSheet';
import { toParams, usePosGet } from '../components/pos/posUtils';

const TABS = [
  ['overview', 'Overview'], ['monthwise', 'Monthwise Analysis'], ['repeat', 'Repeat Customers'],
  ['missing', 'Missing Customers'], ['customers', 'Customer Details'], ['points', 'Points & Settings'],
  ['history', 'Monthly History'], ['imports', 'Import History'],
];

const blank = (latest) => ({
  preset: 'latest', from_month: latest || '', to_month: latest || '', ctype: 'all', q: '',
  min_sales: '', max_sales: '', min_orders: '', min_points_earned: '', min_points_burned: '',
});

export default function PosPlayersPage() {
  const [refresh, setRefresh] = useState(0);
  const { data: meta } = usePosGet('meta', { refresh });
  const latest = meta?.latest_month;
  const [draft, setDraft] = useState(blank());
  const [applied, setApplied] = useState({});
  const [compare, setCompare] = useState(true);
  const [tab, setTab] = useState('overview');
  const [importOpen, setImportOpen] = useState(false);
  const [custId, setCustId] = useState(null);
  const [seeded, setSeeded] = useState(false);

  if (meta && !seeded) {
    setSeeded(true);
    setDraft(blank(latest));
  }
  const params = { ...toParams(applied), refresh };
  const isAdmin = !!meta?.is_admin;
  const onImported = () => {
    setRefresh((r) => r + 1);
    setSeeded(false);
    setApplied({});
  };

  return (
    <DashboardLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pl-12 lg:pl-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-emerald-600" /> POS Players
          </h1>
          <p className="text-slate-500 text-sm mt-0.5" data-testid="pos-subtitle">
            POS customer sales — {meta?.available_months?.length || 0} months imported, {meta?.customer_count || 0} customers.
          </p>
        </div>
        {isAdmin && (
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setImportOpen(true)} data-testid="pos-import-btn">
            <Upload className="w-4 h-4 mr-1" /> Import Excel
          </Button>
        )}
      </div>
      {tab !== 'history' && tab !== 'imports' && (
        <PosFilters draft={draft} setDraft={setDraft} latest={latest} compare={compare} setCompare={setCompare}
          onApply={() => setApplied(draft)} onReset={() => { setDraft(blank(latest)); setApplied({}); }} />
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1 mb-4" data-testid="pos-tabs">
          {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`pos-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview"><OverviewTab params={params} compare={compare} goTab={setTab} openCustomer={setCustId} /></TabsContent>
        <TabsContent value="monthwise"><MonthwiseTab params={params} /></TabsContent>
        <TabsContent value="repeat"><RepeatTab params={params} openCustomer={setCustId} /></TabsContent>
        <TabsContent value="missing"><MissingTab params={params} openCustomer={setCustId} /></TabsContent>
        <TabsContent value="customers"><CustomersTab params={params} openCustomer={setCustId} /></TabsContent>
        <TabsContent value="points"><PointsTab params={params} isAdmin={isAdmin} onSettingsSaved={() => setRefresh((r) => r + 1)} openCustomer={setCustId} /></TabsContent>
        <TabsContent value="history"><HistoryTab latest={latest} openCustomer={setCustId} /></TabsContent>
        <TabsContent value="imports"><ImportHistoryTab isAdmin={isAdmin} openImport={() => setImportOpen(true)} refreshKey={refresh} /></TabsContent>
      </Tabs>
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={onImported} />
      <CustomerSheet id={custId} onClose={() => setCustId(null)} />
    </DashboardLayout>
  );
}
